import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FlaskConical, BookOpen } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingState, ErrorState } from "@/components/ui/empty";
import {
  EntryTypeBadge,
  StatusBadge,
  KindBadge,
} from "@/components/status-badges";
import { Markdown } from "@/components/markdown";
import { useApi } from "@/lib/api";
import { formatFitness, formatRelative } from "@/lib/format";
import type {
  Campaign,
  ExperimentsResponse,
  JournalResponse,
} from "@/lib/types";

export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const campaign = useApi<Campaign>(id ? `/api/campaigns/${id}` : null);
  const experiments = useApi<ExperimentsResponse>("/api/experiments");
  const journal = useApi<JournalResponse>("/api/journal");

  const related = useMemo(() => {
    const exps = (experiments.data?.experiments ?? []).filter(
      (e) => e.campaign_id === id,
    );
    const entries = (journal.data?.entries ?? []).filter(
      (e) => e.campaign_id === id,
    );
    return { exps, entries };
  }, [experiments.data, journal.data, id]);

  if (campaign.loading) return <LoadingState />;
  if (campaign.error) return <ErrorState error={campaign.error} />;
  if (!campaign.data) return null;
  const c = campaign.data;

  return (
    <>
      <Link
        to="/campaigns"
        className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" />
        All campaigns
      </Link>
      <PageHeader
        title={c.name}
        description={`Campaign · started ${formatRelative(c.started_at)} · by ${c.created_by}`}
        actions={<StatusPill status={c.status} />}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
                Lead question
              </CardTitle>
            </CardHeader>
            <CardContent>
              <blockquote className="border-l-2 border-primary/60 pl-4 text-sm italic leading-relaxed">
                {c.lead_question}
              </blockquote>
            </CardContent>
          </Card>

          {c.hypothesis && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
                  Hypothesis
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm leading-relaxed text-muted-foreground">
                <Markdown>{c.hypothesis}</Markdown>
              </CardContent>
            </Card>
          )}

          {c.pass_criterion && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
                  Pass criterion
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm leading-relaxed text-muted-foreground">
                <Markdown>{c.pass_criterion}</Markdown>
              </CardContent>
            </Card>
          )}

          {c.finding && (
            <Card className="border-emerald-500/30">
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wider text-emerald-300">
                  Finding
                </CardTitle>
                <CardDescription>
                  Closed {formatRelative(c.closed_at)}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Markdown>{c.finding}</Markdown>
              </CardContent>
            </Card>
          )}

          {/* Experiments */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FlaskConical className="h-4 w-4" />
                    Experiments
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {related.exps.length} total
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {related.exps.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No experiments linked to this campaign yet.
                </p>
              ) : (
                related.exps.map((e) => (
                  <Link
                    key={e.id}
                    to={`/experiments/${e.id}`}
                    className="flex items-center justify-between gap-2 rounded-md px-2 py-2 text-xs hover:bg-accent"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <StatusBadge status={e.status} />
                      <KindBadge kind={e.kind} />
                      <span className="font-mono">{e.rung_id}</span>
                      <span className="text-muted-foreground">
                        @ {e.engine_version}
                      </span>
                    </div>
                    <span className="shrink-0 font-mono tabular-nums text-muted-foreground">
                      {formatFitness(e.final_fitness)}
                    </span>
                  </Link>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Metadata</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              <Meta
                label="Layer focus"
                value={c.layer_focus !== null ? `L${c.layer_focus}` : "—"}
              />
              <Meta label="Ladder" value={c.primary_ladder ?? "—"} />
              <Meta
                label="Engine repo"
                value={c.primary_engine_repo ?? "—"}
              />
              <Meta label="Created by" value={c.created_by} />
              <Meta
                label="Started"
                value={formatRelative(c.started_at)}
              />
              {c.closed_at && (
                <Meta label="Closed" value={formatRelative(c.closed_at)} />
              )}
              {c.experiment_counts && (
                <div className="mt-2 flex gap-1">
                  <Badge variant="secondary" className="font-mono">
                    {c.experiment_counts.total} exp
                  </Badge>
                  {c.experiment_counts.passed > 0 && (
                    <Badge variant="success" className="font-mono">
                      {c.experiment_counts.passed} passed
                    </Badge>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Journal entries */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Journal ({related.entries.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2.5">
              {related.entries.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No journal entries yet.
                </p>
              ) : (
                related.entries.slice(0, 8).map((e) => (
                  <Link
                    key={e.id}
                    to={`/journal/${e.id}`}
                    className="block space-y-1 rounded-md px-2 py-2 hover:bg-accent"
                  >
                    <EntryTypeBadge entryType={e.entry_type} />
                    <div className="line-clamp-2 text-xs font-medium leading-tight">
                      {e.title}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {formatRelative(e.timestamp)}
                    </div>
                  </Link>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono text-right">{value}</span>
    </div>
  );
}

function StatusPill({ status }: { status: Campaign["status"] }) {
  const styles =
    status === "open"
      ? "border-violet-500/40 bg-violet-500/10 text-violet-300"
      : status === "closed"
        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
        : "border-zinc-500/40 bg-zinc-500/10 text-zinc-400";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${styles}`}
    >
      {status === "open" && (
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-violet-400" />
      )}
      {status}
    </span>
  );
}
